/**
 * Polymarket CTF Exchange EIP-712 signing.
 *
 * Ported from axis-mono's `provider-polymarket/src/signing.ts`.
 * Browser-compatible — uses Web Crypto + viem's `signTypedData` only,
 * no `node:crypto`.
 *
 * We always use signatureType = 0 (EOA) — the user signs the order with their
 * own wallet. POLY_PROXY (1) and POLY_GNOSIS_SAFE (2) variants are not used
 * here.
 */

// CTF Exchange contract addresses (Polygon mainnet, chain 137)
export const CTF_EXCHANGE_ADDRESS =
  "0x4bFb41d5B3570DeFd03C39a9A4D8dE6Bd8B8982E" as const;
export const NEG_RISK_CTF_EXCHANGE_ADDRESS =
  "0xC5d563A36AE78145C45a50134d48A1215220f80a" as const;

/**
 * CTF fee rate default. Polymarket dropped all taker fees to 0 — the on-chain
 * order's `feeRateBps` MUST match the market's current fee exactly, otherwise
 * the CLOB rejects with `invalid fee rate (x), current market's taker fee: y`.
 */
export const DEFAULT_FEE_RATE_BPS = 0n;

export const CTF_CHAIN_ID = 137;

export function getCTFExchangeDomain(negRisk: boolean) {
  return {
    name: "Polymarket CTF Exchange",
    version: "1",
    chainId: CTF_CHAIN_ID,
    verifyingContract: (negRisk
      ? NEG_RISK_CTF_EXCHANGE_ADDRESS
      : CTF_EXCHANGE_ADDRESS) as `0x${string}`,
  } as const;
}

export const CTF_ORDER_TYPES = {
  Order: [
    { name: "salt", type: "uint256" },
    { name: "maker", type: "address" },
    { name: "signer", type: "address" },
    { name: "taker", type: "address" },
    { name: "tokenId", type: "uint256" },
    { name: "makerAmount", type: "uint256" },
    { name: "takerAmount", type: "uint256" },
    { name: "expiration", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "feeRateBps", type: "uint256" },
    { name: "side", type: "uint8" },
    { name: "signatureType", type: "uint8" },
  ],
} as const;

/** ClobAuth domain used when deriving API keys via /auth/api-key. */
export const CLOB_AUTH_DOMAIN = {
  name: "ClobAuthDomain",
  version: "1",
  chainId: CTF_CHAIN_ID,
} as const;

export const CLOB_AUTH_TYPES = {
  ClobAuth: [
    { name: "address", type: "address" },
    { name: "timestamp", type: "string" },
    { name: "nonce", type: "uint256" },
    { name: "message", type: "string" },
  ],
} as const;

/**
 * Minimal signer shape — compatible with viem's `WalletClient`,
 * `PrivateKeyAccount`, and any other thing exposing `signTypedData`.
 *
 * The `signTypedData` signature here is intentionally permissive (Record<string, unknown>)
 * so we can pass viem's strongly-typed structures through without each caller
 * needing to fight TypeScript's literal-type unification.
 */
export type OrderSigner = {
  address: string;
  signTypedData: (params: {
    domain: Record<string, unknown>;
    types: Record<
      string,
      ReadonlyArray<{ name: string; type: string }>
    >;
    primaryType: string;
    message: Record<string, unknown>;
  }) => Promise<string>;
};

export const SIGNATURE_TYPE = {
  EOA: 0,
  POLY_PROXY: 1,
  GNOSIS_SAFE: 2,
} as const;
export type SignatureType = (typeof SIGNATURE_TYPE)[keyof typeof SIGNATURE_TYPE];

export type ClobOrderSide = "BUY" | "SELL";
export type ClobTimeInForce = "GTC" | "GTD" | "FOK" | "IOC";

export type ClobOrderRequest = {
  tokenId: string;
  side: ClobOrderSide;
  /** Decimal cents-as-fraction string, e.g. "0.55" for 55¢. */
  price: string;
  /** Decimal share count, e.g. "100". */
  size: string;
  timeInForce: ClobTimeInForce;
  /** Unix seconds. 0 = no expiration (only valid for GTC). */
  expiration?: number;
};

export type CTFOrder = {
  salt: bigint;
  maker: string;
  signer: string;
  taker: string;
  tokenId: bigint;
  makerAmount: bigint;
  takerAmount: bigint;
  expiration: bigint;
  nonce: bigint;
  feeRateBps: bigint;
  side: number;
  signatureType: number;
};

export type SignedClobOrder = {
  order: CTFOrder;
  signature: string;
};

/**
 * Build a CTF Exchange order struct from a human-readable order request.
 *
 * 6-decimal fixed-point:
 *   BUY:  makerAmount = price*size USDC, takerAmount = size shares
 *   SELL: makerAmount = size shares,      takerAmount = price*size USDC
 *
 * For EOA signing (the only mode we use here), `maker` and `signer` are both
 * the user's EOA address.
 */
export function buildCTFOrder(
  request: ClobOrderRequest,
  maker: string,
  signer: string,
  signatureType: SignatureType = SIGNATURE_TYPE.EOA,
): CTFOrder {
  const price = parseFloat(request.price);
  const size = parseFloat(request.size);
  const isBuy = request.side === "BUY";

  const rawMaker = isBuy ? price * size : size;
  const rawTaker = isBuy ? size : price * size;
  const makerAmount = BigInt(Math.round(rawMaker * 1e6));
  const takerAmount = BigInt(Math.round(rawTaker * 1e6));

  // Polymarket's CLOB parses `salt` as int64; a full 256-bit random value
  // overflows and triggers "Invalid order payload". Match the reference
  // clob-client generator: a random 10-digit integer.
  const salt = BigInt(Math.floor(Math.random() * 1e10));

  return {
    salt,
    maker,
    signer,
    taker: "0x0000000000000000000000000000000000000000",
    tokenId: BigInt(request.tokenId),
    makerAmount,
    takerAmount,
    expiration: request.expiration ? BigInt(request.expiration) : 0n,
    nonce: BigInt(Date.now()),
    feeRateBps: DEFAULT_FEE_RATE_BPS,
    side: isBuy ? 0 : 1,
    signatureType,
  };
}

export async function signCTFOrder(
  order: CTFOrder,
  signer: OrderSigner,
  negRisk: boolean,
): Promise<SignedClobOrder> {
  const domain = getCTFExchangeDomain(negRisk);
  const signature = await signer.signTypedData({
    domain: domain as unknown as Record<string, unknown>,
    types: CTF_ORDER_TYPES as unknown as Record<
      string,
      ReadonlyArray<{ name: string; type: string }>
    >,
    primaryType: "Order",
    message: {
      salt: order.salt,
      maker: order.maker,
      signer: order.signer,
      taker: order.taker,
      tokenId: order.tokenId,
      makerAmount: order.makerAmount,
      takerAmount: order.takerAmount,
      expiration: order.expiration,
      nonce: order.nonce,
      feeRateBps: order.feeRateBps,
      side: order.side,
      signatureType: order.signatureType,
    },
  });
  return { order, signature };
}

/** Sign the ClobAuth attestation used to create/derive an API key trio. */
export async function signClobAuth(
  signer: OrderSigner,
  nonce = 0,
): Promise<{ signature: string; timestamp: string; nonce: string }> {
  const timestamp = String(Math.floor(Date.now() / 1000));
  const nonceStr = String(nonce);
  const signature = await signer.signTypedData({
    domain: CLOB_AUTH_DOMAIN as unknown as Record<string, unknown>,
    types: CLOB_AUTH_TYPES as unknown as Record<
      string,
      ReadonlyArray<{ name: string; type: string }>
    >,
    primaryType: "ClobAuth",
    message: {
      address: signer.address,
      timestamp,
      nonce,
      message: "This message attests that I control the given wallet",
    },
  });
  return { signature, timestamp, nonce: nonceStr };
}
